import { useState } from "react";
import { downloadUrl, thumbUrl } from "../api";
import type { ImageItem } from "../types";
import { IconDownload, IconExpand, IconImage, IconStar, IconTag, IconTrash } from "./icons";
import { TagEditor } from "./TagEditor";
import { Spinner, cx } from "./ui";

export function ratioStyle(aspect: string): string {
  const [w, h] = aspect.split(":");
  return `${w} / ${h}`;
}

function ImageCard({
  image,
  hoverTitle,
  knownTags,
  onOpen,
  onToggleFavorite,
  onQuote,
  onDelete,
  onSaveTags,
}: {
  image: ImageItem;
  /** ギャラリー表示時に元バッチ名をツールチップで出す */
  hoverTitle?: string;
  knownTags?: string[];
  onOpen: () => void;
  onToggleFavorite: () => void;
  onQuote?: () => void;
  onDelete?: () => void;
  onSaveTags?: (tags: string[]) => void;
}) {
  const done = image.status === "done" && image.file_name;
  const [tagOpen, setTagOpen] = useState(false);

  return (
    <figure
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100"
      style={{ aspectRatio: ratioStyle(image.aspect_ratio) }}
      title={hoverTitle}
    >
      {done ? (
        <>
          <img
            src={thumbUrl(image.file_name!, 400)}
            alt={`案 ${image.idx + 1}`}
            loading="lazy"
            onClick={onOpen}
            className="pop-in h-full w-full cursor-zoom-in object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/55 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 p-2 opacity-0 transition group-hover:opacity-100">
            <span className="rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 backdrop-blur">
              {image.width && image.height ? `${image.width}×${image.height}` : image.size}
            </span>
            <div className="ml-auto flex gap-1">
              {onQuote && (
                <button
                  type="button"
                  onClick={onQuote}
                  title="この画像を参照画像として引用（i2iの元にする）"
                  className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-brand-600 backdrop-blur transition hover:bg-white"
                >
                  <IconImage className="h-3.5 w-3.5" />
                </button>
              )}
              {onSaveTags && (
                <button
                  type="button"
                  onClick={() => setTagOpen((v) => !v)}
                  title="タグを付ける"
                  className={cx(
                    "grid h-7 w-7 place-items-center rounded-lg backdrop-blur transition",
                    image.tags.length > 0
                      ? "bg-brand-500 text-white hover:bg-brand-600"
                      : "bg-white/90 text-slate-700 hover:bg-white",
                  )}
                >
                  <IconTag className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={onOpen}
                title="拡大"
                className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-slate-700 backdrop-blur transition hover:bg-white"
              >
                <IconExpand className="h-3.5 w-3.5" />
              </button>
              <a
                href={downloadUrl(image.id)}
                title="ダウンロード"
                className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-slate-700 backdrop-blur transition hover:bg-white"
              >
                <IconDownload className="h-3.5 w-3.5" />
              </a>
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  title="この1枚を削除"
                  className="grid h-7 w-7 place-items-center rounded-lg bg-white/90 text-slate-500 backdrop-blur transition hover:bg-white hover:text-rose-600"
                >
                  <IconTrash className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleFavorite}
            title="お気に入り（★だけZIPできます）"
            className={cx(
              "absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-lg backdrop-blur transition",
              image.favorite
                ? "bg-amber-400 text-white opacity-100"
                : "bg-white/90 text-slate-500 opacity-0 hover:text-amber-500 group-hover:opacity-100",
            )}
          >
            <IconStar className="h-3.5 w-3.5" filled={image.favorite} />
          </button>
        </>
      ) : image.status === "error" ? (
        <div className="flex h-full flex-col items-center justify-center gap-1.5 bg-rose-50 p-3 text-center">
          <span className="text-[13px] font-bold text-rose-600">生成できませんでした</span>
          <span className="line-clamp-4 text-[11.5px] leading-relaxed text-rose-500/90">{image.error}</span>
        </div>
      ) : (
        <div className="relative flex h-full items-center justify-center overflow-hidden bg-slate-200/70">
          <div className="shimmer absolute inset-0" />
          {image.status === "running" ? (
            <div className="z-10 flex flex-col items-center gap-1.5 text-slate-500">
              <Spinner className="h-5 w-5" />
              <span className="text-[12px] font-semibold">生成中…</span>
            </div>
          ) : (
            <span className="z-10 text-[12px] font-semibold text-slate-400">待機中</span>
          )}
        </div>
      )}

      <figcaption className="absolute left-2 right-11 top-2 flex flex-wrap gap-1">
        <span className="rounded bg-slate-900/60 px-1.5 py-0.5 text-[11px] font-bold text-white backdrop-blur">
          #{image.idx + 1}
        </span>
        <span className="rounded bg-slate-900/45 px-1.5 py-0.5 text-[11px] font-semibold text-white/90 backdrop-blur">
          {image.aspect_ratio}
        </span>
        {image.variant_label && (
          <span className="rounded bg-brand-500/85 px-1.5 py-0.5 text-[12px] font-bold text-white backdrop-blur">
            {image.variant_label}
          </span>
        )}
        {image.tags.length > 0 && (
          <span className="rounded bg-slate-900/45 px-1.5 py-0.5 text-[12px] font-semibold text-white/90 backdrop-blur">
            🏷 {image.tags.slice(0, 2).join("・")}
            {image.tags.length > 2 && ` +${image.tags.length - 2}`}
          </span>
        )}
      </figcaption>

      {tagOpen && onSaveTags && (
        <div className="absolute inset-x-1.5 bottom-11 z-20">
          <TagEditor
            tags={image.tags}
            known={knownTags ?? []}
            onSave={onSaveTags}
            onClose={() => setTagOpen(false)}
          />
        </div>
      )}
    </figure>
  );
}

export function ResultGrid({
  images,
  groupByModel,
  modelLabels,
  knownTags,
  hoverTitle,
  onOpen,
  onToggleFavorite,
  onQuote,
  onDelete,
  onSaveTags,
}: {
  images: ImageItem[];
  groupByModel: boolean;
  modelLabels: Record<string, string>;
  knownTags?: string[];
  /** ギャラリー表示時に元バッチ名などを出す */
  hoverTitle?: (image: ImageItem) => string | undefined;
  onOpen: (image: ImageItem) => void;
  onToggleFavorite: (image: ImageItem) => void;
  onQuote?: (image: ImageItem) => void;
  onDelete?: (image: ImageItem) => void;
  onSaveTags?: (image: ImageItem, tags: string[]) => void;
}) {
  const card = (img: ImageItem) => (
    <ImageCard
      key={img.id}
      image={img}
      hoverTitle={hoverTitle?.(img)}
      knownTags={knownTags}
      onOpen={() => onOpen(img)}
      onToggleFavorite={() => onToggleFavorite(img)}
      onQuote={onQuote ? () => onQuote(img) : undefined}
      onDelete={onDelete ? () => onDelete(img) : undefined}
      onSaveTags={onSaveTags ? (tags) => onSaveTags(img, tags) : undefined}
    />
  );

  if (!groupByModel) {
    return <div className="grid grid-cols-[repeat(auto-fill,minmax(285px,1fr))] gap-3.5">{images.map(card)}</div>;
  }

  const groups = new Map<string, ImageItem[]>();
  for (const img of images) {
    const list = groups.get(img.model) ?? [];
    list.push(img);
    groups.set(img.model, list);
  }

  return (
    <div className="space-y-6">
      {[...groups.entries()].map(([model, list]) => (
        <section key={model}>
          <h3 className="mb-2 flex items-center gap-2 text-[14px] font-bold text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
            {modelLabels[model] ?? model}
            <span className="text-[12.5px] font-medium text-slate-400">{list.length}枚</span>
          </h3>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(285px,1fr))] gap-3.5">{list.map(card)}</div>
        </section>
      ))}
    </div>
  );
}
